// Shipping and journey shapes for one order, read off the e166 S5 contract
// (.plans/s5-shipments/contract.md). Both instances, identical.

export type ShippingState = 'not_shipped' | 'partly' | 'shipped';

export type ShipDirection = 'out' | 'return';

/** The order-level reading, also the `shipping` field on each row of GET /orders. */
export interface ShippingReadingSummary {
  state: ShippingState;
  shipped: number;
  total: number;
  /** What came back. Shipped never drops on a return; this is read beside it. */
  returned?: number;
}

export interface ShippingReading extends ShippingReadingSummary {
  /** A sale marked shipped before shipment lines existed. */
  legacy?: boolean;
}

export interface ShippedSerial {
  serial: string;
  direction: ShipDirection;
  shipped_on: string | null;
  shipment_id: string;
  unmatched: boolean;
}

export interface ShippingLine {
  sales_order_line_id: string;
  item_type: string;
  label: string;
  serialized: boolean;
  ordered: number;
  out: number;
  returned: number;
  shipped: number;
  remaining: number;
  serials: ShippedSerial[];
}

export interface ShipmentLine {
  item_type: string;
  label: string;
  drone_serial: string | null;
  quantity: number;
  direction: ShipDirection;
  unmatched: boolean;
}

export interface OrderShipment {
  id: string;
  shipped_on: string | null;
  tracking_number: string | null;
  notes: string | null;
  direction: ShipDirection | 'mixed' | null;
  created_by_name: string | null;
  lines: ShipmentLine[];
  slip_url?: string | null;
}

export interface Shipping {
  order_id: string;
  status: string;
  reading: ShippingReading;
  lines: ShippingLine[];
  shipments: OrderShipment[];
}

export type JourneyKey = 'ordered' | 'picked' | 'built' | 'packed' | 'shipped';

export interface JourneyStage {
  key: JourneyKey;
  done: boolean;
  detail?: string | null;
  n?: number;
  total?: number;
  shipped?: number;
}

export interface Journey {
  stages: JourneyStage[];
  mismatch: string | null;
}

export interface ShipBody {
  direction: ShipDirection;
  shipped_on?: string;
  tracking_number?: string;
  notes?: string;
  serials: string[];
  counts: { sales_order_line_id: string; quantity: number }[];
  confirm?: boolean;
  /** The badged operator on a shared scanner. */
  operator_id?: string;
}

export interface ShipResult {
  shipment_id: string;
  shipping: Shipping;
  status: string;
}

export interface ShipWarning {
  serial: string;
  code: string;
  expected: string;
}

/** "Partly shipped 3 of 5", "Shipped", "Shipped, 1 returned", or null when nothing has gone out. */
export function readingText(r: ShippingReadingSummary | null | undefined): string | null {
  if (!r || r.state === 'not_shipped') return null;
  const base = r.state === 'shipped' ? 'Shipped' : `Partly shipped ${r.shipped} of ${r.total}`;
  return r.returned ? `${base}, ${r.returned} returned` : base;
}

/** The same reading with the empty case spelled out, for the order page. */
export function readingTextOrNot(r: ShippingReadingSummary | null | undefined): string {
  return readingText(r) ?? 'Not shipped';
}

interface ApiErrorBody {
  code?: string;
  message?: string;
  serial?: string;
  /** drone_flagged, drone_not_found and drone_archived name every offending serial here. */
  serials?: string[];
  warnings?: ShipWarning[];
  missing?: { sales_order_line_id?: string; label: string; ordered: number; shipped: number; returned?: number }[];
  shipped?: number;
  /** The contract writes details both spread into the error and under `details`; both are read. */
  details?: Omit<ApiErrorBody, 'details' | 'code' | 'message'>;
}

/** The `{ error: { code, message, ...details } }` body apiFetchAuthed leaves on `err.data`. */
export function shipError(err: unknown): ApiErrorBody {
  const body = (err as { data?: { error?: ApiErrorBody } } | null)?.data?.error;
  return body && typeof body === 'object' ? body : {};
}

/**
 * A sentence for the person at the bench. The server's message wins and is
 * expected to name the drone; when it does not, the serial from the details is
 * put in front so the person knows which scan to look at.
 */
export function shipErrorMessage(err: unknown, fallback = 'The shipment was not recorded. Try again.'): string {
  const e = shipError(err);
  const serial = e.serial ? String(e.serial) : '';
  const named = (text: string) => (serial && !text.includes(serial) ? `${serial}: ${text}` : text);
  if (e.message) return named(e.message);
  switch (e.code) {
    case 'over_ship':
      return 'That is more than the order still owes. Lower the count or remove a serial.';
    case 'order_locked':
      return 'This order has shipped in full, so nothing more can go out. Returns are still open.';
    case 'drone_not_found':
      return named('No drone has that serial. Check the sticker and scan it again.');
    case 'drone_archived':
      return named('This drone is archived and cannot ship. Take it off the list.');
    case 'nothing_to_ship':
      return 'Scan a serial or enter a count first.';
    case 'no_customer':
      return 'This order has no customer yet. Add one on the edit page, then ship.';
    case 'drone_flagged':
      return named('This drone is flagged and cannot ship. Pick another drone, or clear the flag first.');
    case 'duplicate_serial':
      return named('A serial is on the list twice. Remove one.');
    case 'serial_required':
      return 'Drones ship by serial. Scan each one instead of entering a count.';
    case 'not_a_stock_line':
      return 'That line is a service or charge and never ships.';
    default:
      return err instanceof Error && err.message ? err.message : fallback;
  }
}

/** What the record said about one scanned drone, to follow the serial in a sentence. */
export function warningDetail(w: ShipWarning): string {
  switch (w.code) {
    case 'not_held_by_customer':
      return `is not held by this customer (${w.expected})`;
    case 'held_by_other_customer':
    case 'already_out':
      return `is ${w.expected}`;
    case 'wrong_model':
      return `is another model: ${w.expected}`;
    case 'sale_return':
      return `was ${w.expected}`;
    default:
      return `the record expected: ${w.expected}`;
  }
}

/** The warnings of a 409 needs_confirm, or null when the error is anything else. */
export function shipWarnings(err: unknown): ShipWarning[] | null {
  const e = shipError(err);
  if (e.code !== 'needs_confirm') return null;
  const list = e.warnings ?? e.details?.warnings;
  return Array.isArray(list) ? list : [];
}

/** The lines a refused hand move to shipped says are not covered, or an empty list. */
export function shipMissing(err: unknown): NonNullable<ApiErrorBody['missing']> {
  const e = shipError(err);
  if (e.code !== 'not_covered') return [];
  return e.missing ?? e.details?.missing ?? [];
}

/** Lines a hand move to shipped is missing, as one sentence. */
export function missingLinesText(missing: NonNullable<ApiErrorBody['missing']>): string {
  return missing.map((m) => `${m.label}, ${m.shipped} of ${m.ordered} shipped`).join('; ');
}

/** What a refused hand move to returned says is still out, or null when the refusal was something else. */
export function returnReasonRequired(err: unknown): { message: string; missing: NonNullable<ApiErrorBody['missing']> } | null {
  const e = shipError(err);
  if (e.code !== 'reason_required') return null;
  return { message: e.message ?? '', missing: e.missing ?? e.details?.missing ?? [] };
}
